import React, { forwardRef, useState } from "react";
import { makeStyles } from "@fluentui/react-components";
import PropTypes from "prop-types";
import { shorthands, mergeClasses } from "@griffel/react";
import FormattedText from "./FormattedText";
import { FeedbackButtons } from "./FeedbackButtons";
import FeedbackForm from "./FeedbackForm";
import ReferencesBlock from "./ReferencesBlock";

const useStyles = makeStyles({
  messageStack: {
    display: "flex",
    flexDirection: "column",
    //alignItems: "flex-start",
    ...shorthands.padding("0px"),
    ...shorthands.gap("5px"),
    //minWidth: "270px",
    height: "auto",
    ...shorthands.flex(1),
    order: 0,
    flexGrow: 0,
    marginTop: "10px",
  },
  messageHeader: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    ...shorthands.padding("0px"),
    ...shorthands.gap("2px"),
    //minWidth: "270px",
    height: "auto",
    ...shorthands.flex(1),
    order: 0,
    alignSelf: "stretch",
    flexGrow: 0,
  },
  messageHeaderUser: {
    justifyContent: "flex-end",
  },
  messageHeaderBot: {
    justifyContent: "flex-start",
  },
  messageStrings: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    ...shorthands.padding("4px"),
    //minWidth: "270px",
    height: "auto",
    //height: "48px",
    ...shorthands.borderRadius("6px"),
    ...shorthands.flex(1),
    order: 1,
    alignSelf: "stretch",
    flexGrow: 0,
  },
  message: {
    display: "flex",
    flexDirection: "row",
    ...shorthands.padding("0px", "0px", "8px"),
    ...shorthands.gap("10px"),
    //minWidth: "262px",
    height: "auto",
    ...shorthands.flex(1),
    order: 0,
    alignSelf: "stretch",
    flexGrow: 0,
  },
  userMessage: {
    backgroundColor: "rgba(86, 151, 151, 0.3)",
    justifyContent: "flex-end",
    alignItems: "flex-end",
    //alignSelf: "end",
    //flexDirection: "row-reverse",
  },
  botMessage: {
    backgroundColor: "#F5F5F5",
    alignItems: "flex-start",
    /*  alignSelf: "start",
    flexDirection: "row",*/
  },
  /*  text: {
    fontSize: "14px",
    lineHeight: "20px",
    ...shorthands.flex("1"),
  },*/
  buttonContainer: {
    marginTop: "5px",
    display: "flex",
    flexDirection: "row",
  },
  iconStyle: {
    width: "12px",
    height: "12px",
    backgroundSize: "cover",
    //backgroundPosition: "center",
    ...shorthands.flex("none"),
    order: 1,
    flexGrow: 0,
  },
  dateTime: {
    height: "14px",
    fontFamily: "Segoe UI",
    fontStyle: "normal",
    fontWeight: 400,
    fontSize: "10px",
    lineHeight: "14px", // 140% of the font size
    textAlign: "right",
    color: "#666666",
    ...shorthands.flex("none"),
    order: 2,
    flexGrow: 0,
  },
});

const encodeUrl = (url) => {
  return url.split("#").join("%23");
};

const ChatMessage = forwardRef(
  ({ message, onAttachReference, onShareReference, onPostReferenceUsage, canAttach },
  ref) => {
  const styles = useStyles();
  if (!message.links) message.links = [];
  const encodedLinks = message.links.map((link) => ({
    ...link,
    url: encodeUrl(link.url),
  }));
  const [attachingIndex, setAttachingIndex] = useState(null);
  const [sharingIndex, setSharingIndex] = useState(null);
  const [copiedIndex, setCopiedIndex] = useState(null);

  const handleAttach = async (link, index) => {
    if (!canAttach || !onAttachReference) return;
    try {
      setAttachingIndex(index);
      await onAttachReference(link);
    } finally {
      setAttachingIndex(null);
    }
  };

  const handleShare = async (link, index) => {
    if (!onShareReference) return;
    try {
      setSharingIndex(index);
      const result = await onShareReference(link);
      if (result && result.copied) {
        setCopiedIndex(index);
        setTimeout(() => setCopiedIndex(null), 1000);
      }
    } catch (e) {
      console.error("Failed to get shareable link:", e);
    } finally {
      setSharingIndex(null);
    }
  };
  return (
    <div ref={ref} key={message.id} className={styles.messageStack} id="messageStack">
      <div
        id="messageHeader"
        className={mergeClasses(
          styles.messageHeader,
          message.sender === "user" ? styles.messageHeaderUser : styles.messageHeaderBot
        )}
      >
        {message.sender === "user" ? <></> : <img src="assets/icon-32.png" className={styles.iconStyle} />}
        <div id="dateTime" className={styles.dateTime}>
          {new Date(message.timestamp).toLocaleString()}
        </div>
      </div>
      <div
        id="messageStrings"
        className={mergeClasses(
          styles.messageStrings,
          message.sender === "user" ? styles.userMessage : styles.botMessage
        )}
      >
        <div className={styles.message}>
          <FormattedText text={message.text} />
        </div>
        {encodedLinks.length > 0 && (
          <ReferencesBlock
            references={encodedLinks}
            canAttach={canAttach}
            onAttachReference={onAttachReference}
            onShareReference={onShareReference}
            onPostReferenceUsage={onPostReferenceUsage}
            attachingIndex={attachingIndex}
            sharingIndex={sharingIndex}
            copiedIndex={copiedIndex}
            onAttach={handleAttach}
            onShare={handleShare}
          />
        )}
        {message.sender === "bot" &&
          message.messageId &&
          FeedbackButtons({ onFeedback: message.onFeedback, message: message })}
      </div>
      {(message.positiveFeedback || message.negativeFeedback) && (
        <FeedbackForm message={message} onFeedback={message.onFeedback} />
      )}
    </div>
  );
});

ChatMessage.displayName = "ChatMessage";

ChatMessage.propTypes = {
  message: PropTypes.object.isRequired,
};

export default ChatMessage;
